"use client";

import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	ColumnOrderState,
	RowData,
	RowSelectionState,
	Updater,
	VisibilityState,
} from "@tanstack/react-table";
import { useIsomorphicLayoutEffect } from "usehooks-ts";
import { useDataTableLocalStorage } from "./data-table-internal-hooks";
import {
	AnyColumnDef,
	DataTableSortDirection,
	DataTableSortState,
} from "./data-table-types";
import { defaultGetRowId } from "./data-table-utils";

// ---------------------------------------------------------------------------
// useDataTableColumnOrder
// ---------------------------------------------------------------------------

type UseDataTableColumnOrderReturn = {
	columnOrder: ColumnOrderState;
	onColumnOrderChange: (update: Updater<ColumnOrderState>) => void;
};

/**
 * Manages column order state, persisting changes to local storage under {@link storageKey}.
 *
 * Stored order is normalized against the current columns so ids added after the
 * user persisted their settings are inserted at their default position.
 */
export function useDataTableColumnOrder<TData extends RowData>(
	columns: AnyColumnDef<TData>[],
	storageKey: string,
): UseDataTableColumnOrderReturn {
	const [storedSettings, setStoredSettings] =
		useDataTableLocalStorage(storageKey);

	const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
		const defaultOrder = columns.reduce(
			(a, c) => (c.id ? [...a, c.id] : a),
			[] as ColumnOrderState,
		);
		const storedOrder = storedSettings.order;

		if (!storedOrder) {
			return defaultOrder;
		}

		const order = storedOrder.filter((id) => defaultOrder.includes(id));
		defaultOrder.forEach((id, index) => {
			if (!order.includes(id)) {
				const previousId = defaultOrder
					.slice(0, index)
					.reverse()
					.find((candidate) => order.includes(candidate));
				const insertAt = previousId ? order.indexOf(previousId) + 1 : 0;
				order.splice(insertAt, 0, id);
			}
		});
		return order;
	});

	const onColumnOrderChange = (update: Updater<ColumnOrderState>) => {
		setColumnOrder((old) => {
			const next = typeof update === "function" ? update(old) : update;
			setStoredSettings((prev) => ({ ...prev, order: next }));
			return next;
		});
	};

	return { columnOrder, onColumnOrderChange };
}

// ---------------------------------------------------------------------------
// useDataTableColumnVisibility
// ---------------------------------------------------------------------------

type UseDataTableColumnVisibilityOptions = {
	/** Column ids that start hidden until the user shows them. */
	hiddenByDefault?: readonly string[];
};

type UseDataTableColumnVisibilityReturn = {
	columnVisibility: VisibilityState;
	onColumnVisibilityChange: (update: Updater<VisibilityState>) => void;
};

const NO_HIDDEN_COLUMNS: readonly string[] = [];

/**
 * Manages column visibility state, persisting changes to local storage under {@link storageKey}.
 *
 * Every current column id gets a default (hidden if listed in `hiddenByDefault`, else visible),
 * including ids that appear after mount. A visibility the user stored for an id wins over its default.
 */
export function useDataTableColumnVisibility<TData extends RowData>(
	columns: AnyColumnDef<TData>[],
	storageKey: string,
	{
		hiddenByDefault = NO_HIDDEN_COLUMNS,
	}: UseDataTableColumnVisibilityOptions = {},
): UseDataTableColumnVisibilityReturn {
	const [storedSettings, setStoredSettings] =
		useDataTableLocalStorage(storageKey);

	const defaultVisibility = useMemo<VisibilityState>(() => {
		const hidden = new Set(hiddenByDefault);
		return columns.reduce(
			(a, c) => (c.id ? { ...a, [c.id]: !hidden.has(c.id) } : a),
			{},
		);
	}, [columns, hiddenByDefault]);

	const [chosenVisibility, setChosenVisibility] = useState<VisibilityState>(
		() => storedSettings.visibility ?? {},
	);

	const columnVisibility = useMemo(
		() => ({ ...defaultVisibility, ...chosenVisibility }),
		[defaultVisibility, chosenVisibility],
	);

	const onColumnVisibilityChange = (update: Updater<VisibilityState>) => {
		setChosenVisibility((old) => {
			const next =
				typeof update === "function"
					? update({ ...defaultVisibility, ...old })
					: update;
			setStoredSettings((prev) => ({ ...prev, visibility: next }));
			return next;
		});
	};

	return { columnVisibility, onColumnVisibilityChange };
}

// ---------------------------------------------------------------------------
// useDataTableInMemorySort
// ---------------------------------------------------------------------------

type SortableValue = string | number | boolean | Date | null;

/** Sorts {@link data} in memory by the given field and direction, with optional custom comparators. */
export function useDataTableInMemorySort<
	T extends object,
	TSortField extends string = Extract<keyof T, string>,
>(
	data: T[],
	sort?: DataTableSortState<TSortField>,
	customSortFns?: { field: TSortField; sortFn: (a: T, b: T) => number }[],
): T[] {
	const sortedData = React.useMemo(() => {
		if (!sort) {
			return data;
		}

		const { field, direction } = sort;

		const customSortFn = customSortFns?.find(
			(sortFn) => sortFn.field === field,
		)?.sortFn;

		const getFieldValue = (obj: T) =>
			(field as string)
				.replaceAll("_", ".")
				.split(".")
				.reduce<unknown>(
					(value, key) =>
						(value as Record<string, unknown> | null | undefined)?.[key] ??
						null,
					obj,
				) as SortableValue;

		const sorted = data.toSorted((a, b) => {
			if (customSortFn) {
				return (
					customSortFn(a, b) *
					(direction === DataTableSortDirection.Asc ? 1 : -1)
				);
			}

			const aValue = getFieldValue(a);
			const bValue = getFieldValue(b);

			if (
				(aValue === null || aValue === undefined) &&
				(bValue === null || bValue === undefined)
			) {
				return 0;
			}
			if (aValue === null || aValue === undefined) {
				return 1;
			}
			if (bValue === null || bValue === undefined) {
				return -1;
			}
			if (aValue === bValue) {
				return 0;
			}

			if (direction === DataTableSortDirection.Asc) {
				return aValue < bValue ? -1 : 1;
			}
			return aValue > bValue ? -1 : 1;
		});

		return sorted;
	}, [data, sort, customSortFns]);

	return sortedData;
}

// ---------------------------------------------------------------------------
// useDataTableRowSelection
// ---------------------------------------------------------------------------

export type SelectionMode = "explicit" | "all-matching-filter";

function findSingleToggledRow(
	prev: RowSelectionState,
	next: RowSelectionState,
): string | null {
	const added = Object.keys(next).filter((key) => next[key] && !prev[key]);
	const removed = Object.keys(prev).filter((key) => prev[key] && !next[key]);
	const toggled = [...added, ...removed];
	return toggled.length === 1 ? toggled[0] : null;
}

function applyRangeSelection(
	prev: RowSelectionState,
	toggledId: string,
	anchorId: string,
	pageRowIds: string[],
): RowSelectionState {
	const startIdx = pageRowIds.indexOf(anchorId);
	const endIdx = pageRowIds.indexOf(toggledId);
	if (startIdx === -1 || endIdx === -1) {
		return {
			...prev,
			[toggledId]: !prev[toggledId] || undefined,
		} as RowSelectionState;
	}

	const [lo, hi] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
	const rangeIds = pageRowIds.slice(lo, hi + 1);

	if (!prev[toggledId]) {
		return { ...prev, ...Object.fromEntries(rangeIds.map((id) => [id, true])) };
	}

	return Object.fromEntries(
		Object.entries(prev).filter(([key]) => !rangeIds.includes(key)),
	) as RowSelectionState;
}

type UseDataTableRowSelectionOptions<TData> = {
	data?: TData[];
	/**
	 * Keys each row, with the same signature as DataTable's own `getRowId`. Pass
	 * the same function to both, and keep it stable. Defaults to
	 * {@link defaultGetRowId}.
	 */
	getRowId?: (row: TData, index: number) => string;
};

type SelectionState<TData> = {
	rowSelection: RowSelectionState;
	mode: SelectionMode;
	dataMap: Record<string, TData>;
};

type UseDataTableRowSelectionReturn<TData> = {
	rowSelection: RowSelectionState;
	onRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
	selectionMode: SelectionMode;
	selectedIds: string[];
	selectedRows: TData[];
	selectAll: () => void;
	clearSelection: () => void;
};

const INITIAL_STATE = {
	rowSelection: {},
	mode: "explicit" as const,
	dataMap: {},
};

const EMPTY_ROW_DATA: never[] = [];

/** Manages row selection with support for shift-click range selection and a "select all" mode. */
export function useDataTableRowSelection<TData>({
	data,
	getRowId = defaultGetRowId,
}: UseDataTableRowSelectionOptions<TData> = {}): UseDataTableRowSelectionReturn<TData> {
	const rows = data ?? EMPTY_ROW_DATA;
	const [state, setState] = useState<SelectionState<TData>>(
		INITIAL_STATE as SelectionState<TData>,
	);

	const pageRowIds = useMemo(
		() => rows.map((row, i) => getRowId(row, i)),
		[rows, getRowId],
	);

	const shiftKeyRef = useRef(false);
	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === "Shift") {
				shiftKeyRef.current = true;
			}
		};
		const up = (e: KeyboardEvent) => {
			if (e.key === "Shift") {
				shiftKeyRef.current = false;
			}
		};
		window.addEventListener("keydown", down);
		window.addEventListener("keyup", up);
		return () => {
			window.removeEventListener("keydown", down);
			window.removeEventListener("keyup", up);
		};
	}, []);

	const lastToggledRowIdRef = useRef<string | null>(null);

	const dataByKey = useMemo(
		() => Object.fromEntries(rows.map((row, i) => [getRowId(row, i), row])),
		[rows, getRowId],
	);

	// Both are read only from inside `onRowSelectionChange`, which TanStack Table
	// holds across renders and so must keep a stable identity.
	//
	// Layout, not passive: React 19 schedules passive effects as a normal-priority
	// scheduler task and `dispatchDiscreteEvent` does not flush them first, so the
	// browser can paint a new page of rows and deliver a click on one of them
	// while these still hold the previous page. A shift-click would then range
	// over the old id list, and the new row would miss `dataByKey` and never reach
	// `dataMap`. A layout effect closes that window before paint.
	const pageRowIdsRef = useRef(pageRowIds);
	const dataByKeyRef = useRef(dataByKey);
	const [selectedRowsCache, setSelectedRowsCache] = useState<
		Record<string, TData>
	>({});
	useIsomorphicLayoutEffect(() => {
		pageRowIdsRef.current = pageRowIds;
		dataByKeyRef.current = dataByKey;
	}, [pageRowIds, dataByKey]);

	const onRowSelectionChange = useCallback(
		(updater: Updater<RowSelectionState>) => {
			setState((prev) => {
				const isAllMode = prev.mode === "all-matching-filter";
				const anchorId = lastToggledRowIdRef.current;

				const prevSelection = isAllMode
					? Object.fromEntries(
							pageRowIdsRef.current.map((id) => [id, true as const]),
						)
					: prev.rowSelection;

				let next =
					typeof updater === "function" ? updater(prevSelection) : updater;

				const toggledId = findSingleToggledRow(prevSelection, next);
				if (shiftKeyRef.current && anchorId && toggledId) {
					next = applyRangeSelection(
						prevSelection,
						toggledId,
						anchorId,
						pageRowIdsRef.current,
					);
				}
				if (toggledId) {
					lastToggledRowIdRef.current = toggledId;
				}

				const addedSincePrev = isAllMode ? {} : prevSelection;
				const nextDataMap = { ...prev.dataMap };
				let dataMapChanged = false;

				Object.keys(prevSelection).forEach((key) => {
					if (prevSelection[key] && !next[key]) {
						delete nextDataMap[key];
						dataMapChanged = true;
					}
				});

				const lookup = dataByKeyRef.current;
				Object.keys(next).forEach((key) => {
					if (next[key] && !addedSincePrev[key]) {
						const row = lookup[key];
						if (row !== undefined) {
							nextDataMap[key] = row;
							dataMapChanged = true;
						}
					}
				});

				return {
					rowSelection: next,
					mode: isAllMode ? ("explicit" as const) : prev.mode,
					dataMap: dataMapChanged ? nextDataMap : prev.dataMap,
				};
			});
		},
		[],
	);

	const selectAll = useCallback(() => {
		setState({ rowSelection: {}, mode: "all-matching-filter", dataMap: {} });
		lastToggledRowIdRef.current = null;
	}, []);

	const clearSelection = useCallback(() => {
		setState(INITIAL_STATE as SelectionState<TData>);
		lastToggledRowIdRef.current = null;
	}, []);

	const effectiveRowSelection = useMemo(
		() =>
			state.mode === "all-matching-filter"
				? Object.fromEntries(pageRowIds.map((id) => [id, true as const]))
				: state.rowSelection,
		[state.mode, pageRowIds, state.rowSelection],
	);

	const selectedIds = useMemo(
		() =>
			Object.entries(state.rowSelection)
				.filter(([, v]) => v)
				.map(([id]) => id),
		[state.rowSelection],
	);

	useIsomorphicLayoutEffect(() => {
		const selectedIdSet = new Set(selectedIds);
		setSelectedRowsCache((cache) => {
			let changed = false;
			const next = { ...cache };
			for (const id of selectedIds) {
				const live = dataByKey[id];
				if (live !== undefined && next[id] !== live) {
					next[id] = live;
					changed = true;
				}
			}
			for (const id of Object.keys(next)) {
				if (!selectedIdSet.has(id)) {
					delete next[id];
					changed = true;
				}
			}
			return changed ? next : cache;
		});
	}, [dataByKey, selectedIds]);

	const selectedRows = useMemo(
		() =>
			selectedIds
				.map(
					(key) =>
						dataByKey[key] ?? selectedRowsCache[key] ?? state.dataMap[key],
				)
				.filter((row): row is TData => row !== undefined),
		[selectedIds, dataByKey, state.dataMap, selectedRowsCache],
	);

	return {
		rowSelection: effectiveRowSelection,
		onRowSelectionChange,
		selectionMode: state.mode,
		selectedIds,
		selectedRows,
		selectAll,
		clearSelection,
	};
}
